import { useFormik } from 'formik';
import styled, { css } from 'styled-components';
import { Select, TextFiled, TextArea } from 'tsx/comps/form';
import { media, colors } from 'tsx/theme';
import * as Yup from 'yup';
import { useState } from 'react';
import { apiUrl } from 'tsx/util';

const Section = styled.section`
  .header-small {
    display: none;
    margin-bottom: 20px;
  }
  .sendUsSuccess {
    .header-large {
      margin-bottom: 45px;
      h2 {
        margin-bottom: 25px;
      }
    }
    p {
      margin-bottom: 20px;
    }
  }

  .grid {
    display: grid;
    .text-sm {
      margin-bottom: 10px;
    }
    &__img {
      border-radius: 25px;
      overflow: hidden;
      position: relative;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &__contents {
        padding: 27px 30px;
        position: absolute;
        top: 0;
        width: 100%;
        display: flex;
        flex-direction: column;
        height: 100%;

        &__title {
          max-width: 250px;
        }
        &__mobile {
          color: ${colors.primary};
          margin-top: 10px;
        }
        &__ratings-badge {
          margin-top: auto;
          max-height: 220px;
          max-width: 220px;
        }
      }
    }

    form {
      .decor-divider {
        margin: 20px 0;
      }

      .input-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 18px;
        margin: 30px 0 50px 0;
      }

      button {
        min-width: 230px;
        max-width: 230px;
      }
    }
  }
  .input-grid-inner {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  ${media.lg(css`
    .grid {
      grid-gap: 20px;
    }
  `)};

  ${media.md(css`
    .grid {
      grid-gap: 30px;

      &__img {
        &__contents {
          padding: 18px 18px;
          &__ratings-badge {
            max-height: 140px;
            max-width: 140px;
          }
        }
      }
      form {
        .input-grid {
          grid-gap: 10px;
          margin: 20px 0 20px 0;

          input,
          select,
          textarea {
            font-size: 12px;
          }
        }
        button {
          min-width: 160px;
          max-width: 160px;
        }
      }
    }
  `)};

  ${media.sm(css`
    .header-small {
      display: block;
    }
    .sendUsSuccess {
      .header-large {
        h2 {
          margin-bottom: 25px;
        }
      }
      p {
        margin-bottom: 20px;
      }
    }
    .decor-divider {
      margin: 10px 0;
    }
    .grid {
      grid-template-columns: 1fr;
      grid-gap: 10px;

      &__img {
        max-height: 300px;
        border-radius: 18px;
      }
      form {
        .header-large {
        }
        .input-grid {
          grid-gap: 10px;
          margin: 0px 0 20px 0;
          grid-template-columns: 1fr;
        }
      }
    }
  `)};
`;

const validationSchema = Yup.object({
  charityName: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
  contactFirstName: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
  contactLastName: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
  contactEmailAddress: Yup.string().email('Invalid email address').required('Required'),
  contactPhoneNumber: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
  address: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
  overview: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
  donationUse: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
  donationBenefit: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
});

const App = () => {
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const { values, handleChange, handleBlur, errors, touched, isValid, handleSubmit, isSubmitting } = useFormik({
    validationSchema,
    initialValues: {
      charityName: '',
      contactFirstName: '',
      contactLastName: '',
      contactEmailAddress: '',
      contactPhoneNumber: '',
      address: '',
      overview: '',
      donationUse: '',
      donationBenefit: '',
    },
    onSubmit: async (values) => {
      setFormSubmitted(true);
      values.contactPhoneNumber = values.contactPhoneNumber.toString();
      // console.log(updatedValues);
      try {
        const response = await fetch(`/api/forms/apply-for-donations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        if (!response.ok) {
          console.error('Failed to submit form:', response.status, response.statusText);
          const responseData = await response.json();
          console.error('Response data:', responseData);
          return;
        }
      } catch (error) {
        console.error('An error occurred while submitting the form:', error);
      }
    },
  });

  return (
    <Section>
      <div className="grid">
        {!isFormSubmitted ? (
          <form onSubmit={handleSubmit}>
            <div className="header-large">
              <h2>Apply for donations</h2>
              <div className="decor-divider" />
              <p className="text-sm">
                If your charity or group shares our commitments to the local community and you would like to apply for a
                donation, please complete the form below.
              </p>
            </div>

            <div className="input-grid">
              <TextFiled
                placeholder="Name of Charity or Group"
                id="charityName"
                value={values.charityName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.charityName && !!errors.charityName}
              />
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="First Name"
                  id="contactFirstName"
                  value={values.contactFirstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactFirstName && !!errors.contactFirstName}
                />
                <TextFiled
                  placeholder="Last Name"
                  id="contactLastName"
                  value={values.contactLastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactLastName && !!errors.contactLastName}
                />
              </div>
              <TextFiled
                placeholder="Contact Email"
                id="contactEmailAddress"
                type="contactEmailAddress"
                value={values.contactEmailAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.contactEmailAddress && !!errors.contactEmailAddress}
              />
              <TextFiled
                placeholder="Contact Phone"
                id="contactPhoneNumber"
                value={values.contactPhoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.contactPhoneNumber && !!errors.contactPhoneNumber}
                type="number"
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
              <TextArea
                placeholder="Address (including postcode)"
                rows={5}
                id="address"
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.address && !!errors.address}
              />
              <TextArea
                placeholder="Overview of your Charity/Group"
                rows={5}
                id="overview"
                value={values.overview}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.overview && !!errors.overview}
              />
              <TextArea
                placeholder="What would the donation be used for?"
                rows={5}
                id="donationUse"
                value={values.donationUse}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.donationUse && !!errors.donationUse}
              />
              <TextArea
                placeholder="Who would benefit from the donation?"
                rows={5}
                id="donationBenefit"
                value={values.donationBenefit}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.donationBenefit && !!errors.donationBenefit}
              />
            </div>

            <button className="btn btn--large" type="submit" disabled={!isValid || isSubmitting}>
              Submit
            </button>
          </form>
        ) : (
          <div className="sendUsSuccess">
            <div className="header-large">
              <h2>We've got your application</h2>
              <div className="decor-divider" />
            </div>
            <p>Thank you for applying for a Chorley High Five Donation. </p>
            <p>
              Your application has been sent to our Charity Committee for consideration.  Our committee meet four times
              a year, so you will receive a response after the next meeting has taken place.
            </p>
          </div>
        )}
      </div>
    </Section>
  );
};

export default App;
